import { createFileRoute } from '@tanstack/react-router';
import {
  type QueryClient,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { ExternalLinkIcon } from 'lucide-react';

import { Separator } from '@/components/ui/separator';
import {
  FeatureFlag,
  useFeatureFlags,
} from '@/components/providers/FeatureFlagsProvider';
import {
  gitlabConfigQueries,
  gitlabRepositoryQueries,
} from '@/lib/queries/integrations/scm/gitlab';
import { preLoadDefaultProject } from '@/lib/queries/project';

import { GitlabConfigSection } from './-components/GitlabConfigSection';
import { RepositorySection } from './-components/RepositorySection';
import { useRouteLogic } from './-hooks/useGitlabRoute';
import gitlabLogo from '../-components/logos/gitlab.svg?url';

export const Route = createFileRoute('/_root/integrations/gitlab/')({
  component: RouteComponent,
  loader: async ({ context }: { context: { queryClient: QueryClient } }) => {
    const defaultProject = await preLoadDefaultProject({ context });
    context.queryClient.prefetchQuery(
      gitlabConfigQueries.list(defaultProject.id),
    );
    context.queryClient.prefetchQuery(
      gitlabRepositoryQueries.list({
        projectId: defaultProject.id,
        page: 1,
        perPage: 50,
      }),
    );

    return defaultProject;
  },
});

function RouteComponent(): JSX.Element {
  const defaultProject = Route.useLoaderData();
  const queryClient = useQueryClient();
  const featureFlags = useFeatureFlags();

  const { data: gitlabConfigs } = useQuery(
    gitlabConfigQueries.list(defaultProject.id),
  );

  const { data: repositoriesData, isLoading: repositoriesLoading } = useQuery(
    gitlabRepositoryQueries.list({
      projectId: defaultProject.id,
      page: 1,
      perPage: 50,
    }),
  );

  const repositories = repositoriesData?.repositories ?? [];
  const {
    addWebhookMutation,
    removeWebhookMutation,
    filteredRepositories,
    repositorySearchQuery,
    setRepositorySearchQuery,
    refreshRepositories,
  } = useRouteLogic(defaultProject, repositories, queryClient);

  const refreshConfigs = () => {
    queryClient.invalidateQueries(gitlabConfigQueries.list(defaultProject.id));
  };

  const getWebhookOperation = (
    repositoryId: number,
  ): 'adding' | 'removing' | undefined => {
    if (
      addWebhookMutation.isPending &&
      addWebhookMutation.variables === repositoryId
    ) {
      return 'adding';
    }
    if (
      removeWebhookMutation.isPending &&
      removeWebhookMutation.variables === repositoryId
    ) {
      return 'removing';
    }

    return undefined;
  };

  const handleAddWebhook = (repositoryId: number) => {
    addWebhookMutation.mutate(repositoryId);
  };

  const handleRemoveWebhook = (repositoryId: number) => {
    removeWebhookMutation.mutate(repositoryId);
  };

  // Show coming soon state when feature flag is disabled
  if (featureFlags[FeatureFlag.FeatureGitlabSaaS] === false) {
    return (
      <div className="h-full flex flex-col pb-24">
        <h1 className="text-3xl mb-6 flex items-center gap-3">
          <img src={gitlabLogo} alt="GitLab" className="h-8 w-8" />
          GitLab Configuration
        </h1>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center space-y-4 max-w-md">
            <div className="text-6xl">🚧</div>
            <h2 className="text-2xl font-semibold">Coming Soon</h2>
            <p className="text-muted-foreground">
              GitLab SaaS integration is currently under development. Stay tuned
              for updates!
            </p>
            <p className="text-sm text-muted-foreground">
              In the meantime, you can{' '}
              <a
                href="https://gitlab.com"
                target="_blank"
                rel="noreferrer"
                className="underline hover:text-primary"
              >
                visit GitLab <ExternalLinkIcon className="h-4 inline ml-1" />
              </a>{' '}
              to explore their platform.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col pb-24">
      <h1 className="text-3xl mb-6 flex items-center gap-3">
        <img src={gitlabLogo} alt="GitLab" className="h-8 w-8" />
        GitLab Configuration
      </h1>
      <p className="text-muted-foreground mb-8">
        Manage your GitLab integration settings and webhook-enabled
        repositories.{' '}
        <a
          href="https://gitlab.com"
          target="_blank"
          rel="noreferrer"
          className="underline hover:text-primary"
        >
          Visit GitLab
          <ExternalLinkIcon className="h-4 inline ml-1" />
        </a>
      </p>

      <div className="space-y-6">
        <GitlabConfigSection
          gitlabConfigs={gitlabConfigs}
          onRefresh={refreshConfigs}
        />

        <Separator />

        <RepositorySection
          repositories={repositories}
          filteredRepositories={filteredRepositories}
          repositorySearchQuery={repositorySearchQuery}
          setRepositorySearchQuery={setRepositorySearchQuery}
          repositoriesLoading={repositoriesLoading}
          onRefresh={refreshRepositories}
          onAddWebhook={handleAddWebhook}
          onRemoveWebhook={handleRemoveWebhook}
          getWebhookOperation={getWebhookOperation}
        />
      </div>
    </div>
  );
}
